import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { CheckGlyph, delay, tone } from '@/components/agents/primitives';
import {
  SourceIcon,
  type SourceKind,
  SPEAKER_COLORS,
} from '@/components/audio/audioUi';

import { PlayGlyph, SpeakerDot, useLandingT } from './kit';

export type TranscriptLine = {
  /** Index into `speakers`. */
  speaker: number;
  /** Where the line starts, as shown in the app (m:ss or h:mm:ss). */
  time: string;
  /** English key; translated on render. */
  text: string;
};

export type TranscriptInsight = {
  /** Card title, e.g. "Summary", "Action items", "Chapters" (English key). */
  title: string;
  kind: 'bullets' | 'checks' | 'chapters';
  items: { text: string; time?: string }[];
};

/** One example transcript, shown in the hero of a landing page. */
export type TranscriptExample = {
  /** File name or video title. File names are never translated. */
  file: string;
  /** Translate `file` too (a video or meeting title rather than a file name). */
  translateFile?: boolean;
  source: SourceKind;
  /** Language label (English key). */
  language: string;
  duration: string;
  /** Speaker names. People's names are never translated. */
  speakers: string[];
  /** Translate speaker labels (generic roles like "Host" rather than names). */
  translateSpeakers?: boolean;
  lines: TranscriptLine[];
  /** Which line is playing; it gets the highlight. */
  playing?: number;
  insight: TranscriptInsight;
  /**
   * Language of `lines` and the notes when they are written in the spoken
   * language (language pages): they are shown as written, not translated.
   */
  textLang?: string;
};

const SOURCE_LABEL: Record<SourceKind, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  googledrive: 'Google Drive',
  link: 'Link',
  recording: 'Audio recording',
  file: 'Uploaded file',
};

// Bar heights for the player's waveform, fixed so server and client agree.
const WAVE = [
  38, 62, 44, 80, 56, 90, 48, 70, 34, 64, 86, 52, 40, 74, 58, 92, 46, 66, 30,
  60, 82, 50, 72, 42, 68, 88, 54, 36, 76, 48, 62, 40,
];

/**
 * A still of the app's transcript page: header, tabs, player and speaker
 * lines, with the AI notes for the same recording beside it. Decorative: the
 * caller adds an sr-only description.
 */
export default function TranscriptMock({
  example,
  start = 0,
}: {
  example: TranscriptExample;
  /** Entrance delay of the first element, in ms. */
  start?: number;
}) {
  const { t } = useLandingT();
  const playing = example.playing ?? 1;
  const text = (key: string) => (example.textLang ? key : t(key));
  const speakerName = (index: number) => {
    const name = example.speakers[index] ?? '';
    return example.translateSpeakers ? t(name) : name;
  };

  return (
    <div
      aria-hidden='true'
      className='relative mx-auto grid max-w-6xl items-start gap-5 lg:grid-cols-12 lg:gap-0'
    >
      <div className='min-w-0 lg:col-span-8 lg:pe-2'>
        <div
          className={clsxm(
            styles.enter,
            'overflow-hidden rounded-[22px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(22,12,90,0.75)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10',
          )}
          style={delay(start)}
        >
          {/* Header: what was transcribed */}
          <div className='flex items-center gap-3 border-b border-black/[0.06] px-4 py-3.5 sm:px-5 dark:border-white/[0.06]'>
            <SourceIcon kind={example.source} />
            <div className='min-w-0 flex-1'>
              <p
                className={clsxm(
                  'truncate text-[15px] font-semibold',
                  tone.ink,
                )}
              >
                {example.translateFile ? t(example.file) : example.file}
              </p>
              <p className={clsxm('truncate text-xs', tone.quiet)}>
                {t(SOURCE_LABEL[example.source])} · {t(example.language)} ·{' '}
                <span className='tabular-nums'>{example.duration}</span>
              </p>
            </div>
            <span className='flex flex-none items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
              <span className='h-1.5 w-1.5 rounded-full bg-emerald-500' />
              {t('Transcribed')}
            </span>
          </div>

          {/* Tabs, as on the transcript page */}
          <div className='flex items-center gap-1 px-4 pt-4 sm:px-5'>
            <div className='inline-flex rounded-xl bg-[#f3f3f7] p-1 text-[13px] font-medium dark:bg-white/[0.06]'>
              <span
                className={clsxm(
                  'rounded-lg bg-white px-3 py-1.5 shadow-sm dark:bg-white/[0.12]',
                  tone.ink,
                )}
              >
                {t('Transcription')}
              </span>
              <span className={clsxm('px-3 py-1.5', tone.quiet)}>
                {t('AI Content')}
              </span>
              <span
                className={clsxm('hidden px-3 py-1.5 sm:inline', tone.quiet)}
              >
                {t('AI Chat')}
              </span>
            </div>
          </div>

          {/* Player */}
          <div className='mx-4 mt-4 flex items-center gap-3 rounded-2xl bg-[#f7f7fa] px-3 py-2.5 sm:mx-5 dark:bg-white/[0.04]'>
            <span className='flex h-8 w-8 flex-none items-center justify-center rounded-full bg-primary-600 text-white'>
              <PlayGlyph className='h-3.5 w-3.5 translate-x-px' />
            </span>
            <div
              className={clsxm(
                styles.wave,
                styles.waveCenter,
                'flex h-7 min-w-0 flex-1 items-center gap-[3px] overflow-hidden',
              )}
            >
              {WAVE.map((height, index) => (
                <span
                  key={index}
                  className={clsxm(
                    'w-[3px] flex-none rounded-full',
                    index < 12
                      ? 'bg-primary-500'
                      : 'bg-[#d7d7e2] dark:bg-white/15',
                  )}
                  style={
                    {
                      height: `${height}%`,
                      '--d': `${(index % 8) * 90}ms`,
                    } as React.CSSProperties
                  }
                />
              ))}
            </div>
            <span
              className={clsxm('flex-none text-xs tabular-nums', tone.quiet)}
            >
              {example.lines[playing]?.time ?? '0:00'} / {example.duration}
            </span>
          </div>

          {/* Speaker lines */}
          <ol className='space-y-1 px-2 pb-4 pt-3 sm:px-3'>
            {example.lines.map((line, index) => {
              const color =
                SPEAKER_COLORS[line.speaker % SPEAKER_COLORS.length];
              const active = index === playing;
              return (
                <li
                  key={index}
                  className={clsxm(
                    styles.enter,
                    'rounded-2xl px-2 py-2.5 sm:px-3',
                    active &&
                      'bg-primary-50/80 ring-1 ring-inset ring-primary-100 dark:bg-primary-500/10 dark:ring-primary-400/20',
                  )}
                  style={delay(start + 260 + index * 220)}
                >
                  <div className='flex items-center gap-2 text-[13px]'>
                    <SpeakerDot color={color} />
                    <span className={clsxm('font-semibold', tone.ink)}>
                      {speakerName(line.speaker)}
                    </span>
                    <span className={clsxm('text-xs tabular-nums', tone.quiet)}>
                      {line.time}
                    </span>
                  </div>
                  <p
                    className={clsxm(
                      'mt-1 ps-4 text-[14px] leading-relaxed',
                      active ? tone.ink : tone.body,
                    )}
                    dir='auto'
                    lang={example.textLang}
                  >
                    {text(line.text)}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {/* AI notes from the same recording; overlaps only the window's padding. */}
      <div className='grid min-w-0 gap-5 sm:grid-cols-2 lg:col-span-4 lg:-ms-3 lg:mt-14 lg:grid-cols-1'>
        <div
          className={clsxm(styles.enter, 'sm:col-span-2 lg:col-span-1')}
          style={delay(start + 300 + example.lines.length * 220)}
        >
          <InsightCard insight={example.insight} textLang={example.textLang} />
        </div>
        <div
          className={clsxm(styles.enter, 'hidden lg:ms-8 lg:block')}
          style={delay(start + 600 + example.lines.length * 220)}
        >
          <ExportCard />
        </div>
      </div>
    </div>
  );
}

function InsightCard({
  insight,
  textLang,
}: {
  insight: TranscriptInsight;
  textLang?: string;
}) {
  const { t } = useLandingT();

  return (
    <div className='rounded-[20px] bg-white p-5 text-start shadow-[0_30px_70px_-30px_rgba(22,12,90,0.7)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
      <div className='flex items-center justify-between gap-3'>
        <p className={clsxm('text-sm font-semibold', tone.ink)}>
          {t(insight.title)}
        </p>
        <span className='rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary-700 dark:bg-primary-500/15 dark:text-primary-300'>
          {t('AI')}
        </span>
      </div>
      <ul className='mt-3.5 space-y-2.5'>
        {insight.items.map((item, index) => (
          <li
            key={index}
            className={clsxm(
              'flex gap-2.5 text-[13px] leading-snug',
              tone.body,
            )}
          >
            {insight.kind === 'checks' ? (
              <span className='mt-px flex h-4 w-4 flex-none items-center justify-center rounded-[5px] bg-emerald-500 text-white'>
                <CheckGlyph className='h-2.5 w-2.5' />
              </span>
            ) : insight.kind === 'chapters' ? (
              <span className='mt-px flex-none rounded-md bg-[#f3f3f7] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-[#56566d] dark:bg-white/[0.08] dark:text-[#c7c7da]'>
                {item.time}
              </span>
            ) : (
              <span className='mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-primary-500' />
            )}
            <span dir='auto' lang={textLang}>
              {textLang ? item.text : t(item.text)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ExportCard() {
  const { t } = useLandingT();
  // What the transcript page offers: copy the text, subtitles, a share link.
  const formats = [t('Copy text'), 'SRT', 'VTT'];

  return (
    <div className='rounded-[20px] bg-white/95 p-4 text-start shadow-[0_30px_70px_-30px_rgba(22,12,90,0.7)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
      <p className={clsxm('text-xs font-medium', tone.quiet)}>
        {t('Copy, download or share')}
      </p>
      <div className='mt-2.5 flex flex-wrap gap-1.5'>
        {formats.map((format) => (
          <span
            key={format}
            className={clsxm(
              'rounded-lg px-2.5 py-1 text-xs font-semibold',
              tone.ink,
              'bg-[#f3f3f7] dark:bg-white/[0.07]',
            )}
          >
            {format}
          </span>
        ))}
        <span className='rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white'>
          {t('Share link')}
        </span>
      </div>
    </div>
  );
}
